
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { uniqueId } from 'lodash'

export default {
  name: 'Select',

  props: {
    value: {
      type: [String, Number, Array, Object],
      default: ''
    },

    placeholder: {
      type: String,
      default: null
    },

    isFirstOptionEmpty: {
      type: Boolean,
      default: true
    },

    isLoading: {
      type: Boolean,
      default: false
    },

    isDisabled: {
      type: Boolean,
      default: false
    },

    options: {
      type: Array,
      default: () => []
    },

    // When value is empty we show the placeholder text
    optionEmptyValue: {
      type: [String, Number, Array, Object],
      default: null
    },

    optionValueKey: {
      type: String,
      default: 'value'
    },

    optionLabelKey: {
      type: String,
      default: 'label'
    },

    optionGroupKey: {
      type: String,
      default: 'group'
    },

    optionDisabledKey: {
      type: String,
      default: 'disabled'
    },

    // Overrides prefixIcon
    prefixText: {
      type: String,
      default: null
    },

    prefixGrouped: {
      type: Boolean,
      default: false
    },

    prefixIcon: {
      type: Object,
      default: null
    },

    suffixText: {
      type: String,
      default: null
    },

    suffixIcon: {
      type: Object,
      default: null
    },

    // Do not use this directly, use MultiSelect instead
    multiple: {
      type: Boolean,
      default: false
    },

    isTransparent: {
      type: Boolean,
      default: false
    },

    textIsTransparent: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      id: null,
      faChevronDown
    }
  },

  computed: {
    compValue: {
      set(newValue) {
        this.$emit('input', newValue)
      },

      get() {
        return this.value
      }
    },

    compOptions() {
      if (this.options.length) {
        // This allows you to pass a looser flat array from an API e.g ['Yes', 'No'] or [5, 9]
        if (typeof this.options[0] === 'string' || typeof this.options[0] === 'number') {
          return this.options.map(option => {
            return {
              [this.optionLabelKey]: option,
              [this.optionValueKey]: option,
              disabled: this.optionDisabledKey
            }
          })
        } else {
          return this.options
        }
      }

      return []
    },

    // If our grouped options have more than 1 unique group value then return true
    compHasGroups() {
      // It's > 1 to take into account our default group
      return Object.keys(this.compGroupedOptions).length > 1
    },

    // We need a default group in case some options have groups and others don't
    compGroupedOptions() {
      // We can't group flat options
      if (typeof this.options[0] === 'string' || typeof this.options[0] === 'number') {
        return {}
      }

      const groups = { default: [] }

      this.options.forEach(option => {
        if (this.optionGroupKey in option) {
          if (option[this.optionGroupKey] in groups) {
            groups[option[this.optionGroupKey]].push(option)
          } else {
            groups[option[this.optionGroupKey]] = [option]
          }
        } else {
          groups.default.push(option)
        }
      })

      return groups
    },

    compIsLoading() {
      return this.compOptions.length === 0
    },

    compIsDisabled() {
      return this.compIsLoading || this.isDisabled
    },

    compIsEmpty() {
      if (typeof this.value !== 'object') {
        return this.value === this.optionEmptyValue && this.isFirstOptionEmpty
      } else {
        return JSON.stringify(this.value) === JSON.stringify(this.optionEmptyValue) && this.isFirstOptionEmpty
      }
    },

    compHasPrefix() {
      return this.prefixIcon || this.prefixText || this.$slots.prefix
    },

    compShouldSetInitialValue() {
      return !this.isFirstOptionEmpty && !this.value && !this.compIsLoading
    }
  },

  watch: {
    compIsLoading() {
      this.setInitialValue()
    }
  },

  created() {
    this.id = uniqueId('option')

    this.setInitialValue()
  },

  methods: {
    // If there are no empty options we need to set a value
    setInitialValue() {
      // Next tick to ensure options are available
      this.$nextTick(() => {
        if (this.compShouldSetInitialValue && this.compOptions.length) {
          this.compValue = !this.compHasGroups
            ? this.compOptions[0][this.optionValueKey]
            : this.options[0][this.optionValueKey]
        }
      })
    }
  }
}
